import { ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useFileJobs from "files/hooks/useFileJobs";
import useFiles, { resolveRouterPath, useFilesSearch } from "files/hooks/useFiles";
import JobAction from "files/types/enums/jobAction";
import FileJob, { JobStatus } from "files/types/interfaces/fileJob";
import { AlertCircle, RefreshCw, X } from "lucide-react";
import { StringParam, useQueryParam } from "use-query-params";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { ExpandableListItem, ListCard } from "~/components/ui/list-card";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";

const { VITE_RWB_API } = import.meta.env;

const JobDetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <div className="flex gap-1 text-sm">
    <span className="whitespace-nowrap font-medium">{label}:</span>
    <span>{value}</span>
  </div>
);

const JobDetails = ({ job, prefix }: { job: FileJob; prefix?: string }) => {
  const { data, mutate: mutateJobs } = useFileJobs(prefix);
  const { mutate: mutateFiles } = useFiles(prefix);
  const { mutate: mutateFilesSearch } = useFilesSearch();
  const [search] = useQueryParam("q", StringParam);
  const { datasetId } = useParams();
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const isActive = [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status);
  const action = isActive ? JobAction.CANCEL : JobAction.ARCHIVE;
  const ctaLabel = isActive ? "Cancel" : "Clear";
  const jobErrors = job.progress?.filter((file) => file.error_message) ?? [];

  const updateJob = async () => {
    setError(false);
    setSubmitting(true);

    const resp = await fetch(`${VITE_RWB_API}/file-browser/jobs/${job.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action,
      }),
    });

    if (resp.ok) {
      await mutateJobs(data?.filter(({ id }) => id !== job.id));

      if (search) {
        await mutateFilesSearch();
      }
      await mutateFiles();
    } else {
      setError(true);
    }
    setSubmitting(false);
  };

  return (
    <div className="mx-4 flex flex-col gap-2">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>Unable to {ctaLabel.toLowerCase()}</AlertDescription>
        </Alert>
      )}
      <JobDetailRow
        label="Source"
        value={resolveRouterPath(job.source, isGlobalFileContext, datasetId)}
      />
      <JobDetailRow
        label="Destination"
        value={resolveRouterPath(job.destination, isGlobalFileContext, datasetId)}
      />
      {!!jobErrors.length && (
        <JobDetailRow
          label="Errors"
          value={
            <div className="flex flex-col gap-0.5">
              {jobErrors.map(({ source, error_message }) => (
                <div key={source} className="break-all">
                  <span className="font-mono text-inherit">{error_message}</span> -{" "}
                  {resolveRouterPath(source, isGlobalFileContext, datasetId)}
                </div>
              ))}
            </div>
          }
        />
      )}
      <div className="flex justify-center">
        <Button
          variant="ghost"
          className="w-[150px] text-destructive hover:text-destructive"
          loading={submitting}
          onClick={updateJob}
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

interface EmbeddedFileJobsProps {
  /**
   * Explicit directory prefix to revalidate on job completion. Pass this for callers
   * (like PersonalDatasetFiles) that track the browsed folder as local state rather than
   * the URL, since `useFiles()` otherwise resolves the current directory from the route.
   */
  prefix?: string;
}

/**
 * Shadcn rebuild of `files/components/FileJobs`, for the nextgen-embedded
 * Files tab only. Same `useFileJobs` polling/undo logic as the original; only
 * the icon button, dialog list, and notification banner changed.
 */
export const EmbeddedFileJobs = ({ prefix }: EmbeddedFileJobsProps) => {
  const title = "Recent File Actions";
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const [open, setOpen] = useState(false);
  const { data = [], notification } = useFileJobs(prefix);
  const pendingJobs = data.filter((job) =>
    [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status)
  );
  const jobsWithErrors =
    data.filter((job) => {
      return job.progress?.some((progress) => !!progress.error_message);
    }) ?? [];
  // Only show pending or completed (with error) jobs
  const jobsShown = [...pendingJobs, ...jobsWithErrors];

  useEffect(() => {
    if (!jobsShown.length) {
      setOpen(false);
    }
  }, [jobsShown]);

  return (
    <>
      {!!jobsShown.length && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="relative size-8"
              onClick={() => setOpen(true)}
            >
              <RefreshCw
                className={cn("size-4 scale-x-[-1]", pendingJobs.length && "animate-spin")}
              />
              {!!jobsWithErrors.length && (
                <span className="absolute right-1 top-1 size-1.5 rounded-full bg-destructive" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>{title}</TooltipContent>
        </Tooltip>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[500px] flex-col overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription className="sr-only">
              Copy, move, and rename jobs currently in progress or that finished with an error
            </DialogDescription>
          </DialogHeader>
          <ListCard>
            {jobsShown.map((job) => {
              const sourcePath = resolveRouterPath(job.source ?? "", isGlobalFileContext);
              const filename = sourcePath.split("/").at(-1) ?? "";

              return (
                <ExpandableListItem
                  indicator
                  key={job.id}
                  expansionContent={<JobDetails job={job} prefix={prefix} />}
                  icon={
                    [JobStatus.PENDING, JobStatus.RUNNING].includes(job.status) ? (
                      <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
                    ) : (
                      <AlertCircle className="size-3.5 text-destructive" />
                    )
                  }
                >
                  <span className="break-all first-letter:capitalize">
                    {job.job_type} {filename}
                  </span>
                </ExpandableListItem>
              );
            })}
          </ListCard>
        </DialogContent>
      </Dialog>
      {notification.visible && (
        <div className="fixed bottom-4 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 items-center gap-2 rounded-md border border-border bg-foreground px-4 py-3 text-background shadow-lg">
          <span className="flex-1 text-sm">{notification.message}</span>
          {notification.undoCallback && (
            <Button
              variant="ghost"
              size="sm"
              className="text-background hover:bg-background/10 hover:text-background"
              onClick={() => {
                notification.hide();
                notification.undoCallback?.();
              }}
            >
              Undo
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="size-6 text-background hover:bg-background/10 hover:text-background"
            aria-label="close"
            onClick={notification.hide}
          >
            <X className="size-3.5" />
          </Button>
        </div>
      )}
    </>
  );
};
